<template>
    <v-container fluid class="ai-usage pa-6">
        <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
            <div>
                <h1 class="text-h4 font-weight-bold">AI Usage</h1>
                <div class="text-subtitle-1 grey--text">
                    OpenAI spend across DGTasks, the daily budget, and every AI call
                </div>
            </div>
            <v-spacer />
            <v-chip v-if="blockedToday > 0" color="error" dark small>
                <v-icon left x-small>mdi-block-helper</v-icon>
                {{ blockedToday }} blocked today
            </v-chip>
            <span v-else class="caption grey--text">No blocked calls today</span>
            <v-btn outlined color="primary" :loading="loading || breakdownLoading" @click="loadAll">
                <v-icon left small>mdi-refresh</v-icon> Refresh
            </v-btn>
        </div>

        <v-row>
            <!-- Budget -->
            <v-col cols="12" md="6" lg="5">
                <v-card height="100%">
                    <v-card-title>
                        <v-icon left color="primary">mdi-robot</v-icon>
                        Daily AI Budget
                    </v-card-title>
                    <v-card-subtitle>
                        Once today's spend reaches the limit, every AI feature is blocked until midnight
                        (server time). Set to <strong>0</strong> for no limit.
                    </v-card-subtitle>
                    <v-divider />
                    <v-card-text>
                        <v-row class="mb-2" align="center" no-gutters>
                            <v-col>Today's spend</v-col>
                            <v-col class="text-right font-weight-medium">
                                <span v-if="isUnlimited">${{ usd(snapshot.spend_usd) }} / <em>No limit</em></span>
                                <span v-else>
                                    ${{ usd(snapshot.spend_usd) }} / ${{ usd(snapshot.limit_usd) }}
                                    ({{ Math.round(snapshot.percentage) }}%)
                                </span>
                            </v-col>
                        </v-row>
                        <v-progress-linear v-if="!isUnlimited" :value="Math.min(snapshot.percentage, 100)"
                            :color="barColor" background-color="grey lighten-3" height="10" rounded class="mb-2" />
                        <v-alert v-if="isUnlimited" type="info" dense text class="mb-2 caption">
                            No daily limit is set: AI calls are unrestricted.
                        </v-alert>
                        <v-alert v-else-if="snapshot.percentage >= 100" type="error" dense text class="mb-2 caption">
                            Daily limit reached. All AI calls are blocked until midnight.
                        </v-alert>
                        <v-row no-gutters class="caption grey--text">
                            <v-col>
                                Remaining:
                                {{ isUnlimited ? 'Unlimited' : '$' + usd(snapshot.remaining_usd) }}
                            </v-col>
                            <v-col class="text-right">
                                Limit source:
                                <v-chip x-small dark :color="snapshot.source === 'database' ? 'primary' : 'grey'">
                                    {{ snapshot.source === 'database' ? 'This page' : 'Server .env' }}
                                </v-chip>
                            </v-col>
                        </v-row>
                        <div class="caption grey--text mt-1">
                            Server default (.env):
                            {{ snapshot.default_limit_usd ? '$' + usd(snapshot.default_limit_usd) : 'No limit' }}
                        </div>
                    </v-card-text>
                    <v-divider />
                    <v-card-text class="d-flex align-center" style="gap: 12px">
                        <v-text-field v-model.number="limitInput" type="number" min="0" prefix="$"
                            label="Daily AI budget (USD), 0 = no limit" outlined dense hide-details
                            style="max-width: 280px" />
                        <v-spacer />
                        <v-btn color="primary" :loading="saving" :disabled="!canSave" @click="saveLimit">Save</v-btn>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Last 7 days -->
            <v-col cols="12" md="6" lg="7">
                <v-card height="100%">
                    <v-card-title>
                        <v-icon left color="indigo">mdi-chart-bar</v-icon>
                        Last 7 Days
                        <v-spacer />
                        <v-btn-toggle v-model="weekMetric" mandatory dense>
                            <v-btn small value="cost">Cost</v-btn>
                            <v-btn small value="calls">Calls</v-btn>
                        </v-btn-toggle>
                    </v-card-title>
                    <v-card-text>
                        <div v-if="breakdownLoading" class="text-center py-6">
                            <v-progress-circular indeterminate color="primary" />
                        </div>
                        <bar-chart v-else :bars="weekBars" :height="190" color="#5C6BC0" />
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Month to date -->
            <v-col cols="12">
                <v-card>
                    <v-card-title>
                        <v-icon left color="orange darken-1">mdi-calendar-month</v-icon>
                        {{ monthLabel }}: spend per day
                    </v-card-title>
                    <v-card-text>
                        <div v-if="breakdownLoading" class="text-center py-3">
                            <v-progress-circular indeterminate color="orange" size="24" />
                        </div>
                        <template v-else>
                            <bar-chart :bars="monthBars" :height="170" color="#FB8C00" compact />
                            <div class="d-flex flex-wrap mt-3" style="gap: 24px">
                                <span class="caption">Total cost: <strong>${{ usd(monthTotals.cost) }}</strong></span>
                                <span class="caption">Total calls: <strong>{{ monthTotals.calls }}</strong></span>
                                <span class="caption">Active days: <strong>{{ monthTotals.activeDays }}</strong></span>
                                <v-chip v-if="monthProjection !== null" small color="orange lighten-4">
                                    <v-icon x-small left>mdi-trending-up</v-icon>
                                    Projected month: <strong class="ml-1">${{ usd(monthProjection) }}</strong>
                                </v-chip>
                            </div>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Period summary + drill down -->
            <v-col cols="12" md="6">
                <v-card height="100%">
                    <v-card-title>
                        <v-icon left color="orange darken-2">mdi-calendar-clock</v-icon>
                        Period Summary
                        <span class="caption grey--text ml-2">(click to see features)</span>
                    </v-card-title>
                    <v-card-text>
                        <v-row dense>
                            <v-col v-for="p in periods" :key="p.key" cols="4">
                                <div class="text-center pa-3 rounded period-tile"
                                    :style="{ background: selectedPeriod === p.key ? p.color : '#f5f5f5' }"
                                    @click="selectedPeriod = selectedPeriod === p.key ? null : p.key">
                                    <div class="caption" :style="{ color: selectedPeriod === p.key ? '#fff' : '#666' }">{{ p.label }}</div>
                                    <div class="text-h6 font-weight-bold" :style="{ color: selectedPeriod === p.key ? '#fff' : p.color }">
                                        ${{ usd(p.cost) }}
                                    </div>
                                    <div class="caption" :style="{ color: selectedPeriod === p.key ? 'rgba(255,255,255,.85)' : '#999' }">
                                        {{ p.calls }} calls
                                    </div>
                                </div>
                            </v-col>
                        </v-row>
                        <v-expand-transition>
                            <div v-if="selectedPeriod" class="mt-4">
                                <div v-for="f in periodFeatures" :key="f.source" class="d-flex align-center mb-2" style="gap: 8px">
                                    <span class="caption font-weight-medium" style="min-width: 170px">{{ sourceLabel(f.source) }}</span>
                                    <v-progress-linear :value="f.pct" color="orange darken-2" background-color="grey lighten-3"
                                        height="8" rounded class="flex-grow-1" />
                                    <span class="caption grey--text text--darken-1" style="min-width: 110px; text-align: right">
                                        {{ f.calls }} calls · ${{ usd(f.cost) }}
                                    </span>
                                </div>
                                <div v-if="!periodFeatures.length" class="caption grey--text">No activity in this period.</div>
                            </div>
                        </v-expand-transition>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Feature breakdown (all time) -->
            <v-col cols="12" md="6">
                <v-card height="100%">
                    <v-card-title>
                        <v-icon left color="teal">mdi-robot-outline</v-icon>
                        Features (all time)
                    </v-card-title>
                    <v-data-table :headers="featureHeaders" :items="bySource" :loading="breakdownLoading"
                        dense hide-default-footer :items-per-page="-1" sort-by="total_cost" sort-desc>
                        <template v-slot:[`item.source`]="{ item }">{{ sourceLabel(item.source) }}</template>
                        <template v-slot:[`item.today`]="{ item }">${{ usd(item.today.cost) }}</template>
                        <template v-slot:[`item.month`]="{ item }">${{ usd(item.month.cost) }}</template>
                        <template v-slot:[`item.total_cost`]="{ item }"><strong>${{ usd(item.total_cost) }}</strong></template>
                    </v-data-table>
                </v-card>
            </v-col>

            <!-- Custom range -->
            <v-col cols="12">
                <v-card>
                    <v-card-title>
                        <v-icon left color="deep-purple">mdi-calendar-search</v-icon>
                        Custom Date Range
                    </v-card-title>
                    <v-card-text>
                        <v-row align="center" dense>
                            <v-col cols="12" sm="4" md="3">
                                <v-text-field v-model="rangeFrom" type="date" label="From" outlined dense hide-details />
                            </v-col>
                            <v-col cols="12" sm="4" md="3">
                                <v-text-field v-model="rangeTo" type="date" label="To" outlined dense hide-details />
                            </v-col>
                            <v-col cols="auto">
                                <v-btn color="deep-purple" dark :loading="rangeLoading" :disabled="!rangeFrom || !rangeTo" @click="loadRange">
                                    <v-icon left small>mdi-magnify</v-icon> Analyse
                                </v-btn>
                                <v-btn v-if="rangeResults" text class="ml-2" @click="rangeResults = null">Clear</v-btn>
                            </v-col>
                        </v-row>
                        <template v-if="rangeResults">
                            <div class="d-flex flex-wrap mt-4" style="gap: 16px">
                                <div class="stat-tile">
                                    <div class="caption grey--text">Total cost</div>
                                    <div class="text-h6 deep-purple--text">${{ usd(rangeResults.totals.cost) }}</div>
                                </div>
                                <div class="stat-tile">
                                    <div class="caption grey--text">Total calls</div>
                                    <div class="text-h6 deep-purple--text">{{ rangeResults.totals.calls }}</div>
                                </div>
                            </div>
                            <bar-chart v-if="rangeBars.length" class="mt-4" :bars="rangeBars" :height="150" color="#7E57C2" compact />
                            <div class="mt-4">
                                <div v-for="f in rangeResults.by_source" :key="f.source" class="d-flex align-center mb-2" style="gap: 8px">
                                    <span class="body-2 font-weight-medium" style="min-width: 190px">{{ sourceLabel(f.source) }}</span>
                                    <v-progress-linear :value="rangePct(f)" color="deep-purple" background-color="grey lighten-3"
                                        height="10" rounded class="flex-grow-1" />
                                    <span class="caption" style="min-width: 170px; text-align: right">
                                        ${{ usd(f.total_cost) }} · {{ f.calls }} calls · {{ rangePct(f) }}%
                                    </span>
                                </div>
                                <div v-if="!rangeResults.by_source.length" class="caption grey--text">No AI usage in this range.</div>
                            </div>
                        </template>
                    </v-card-text>
                </v-card>
            </v-col>

            <!-- Call log + per user -->
            <v-col cols="12">
                <v-expansion-panels>
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            <div>
                                <v-icon left color="blue-grey">mdi-format-list-bulleted</v-icon>
                                <strong>AI Calls</strong>
                                <span class="caption grey--text ml-2">(call log, for troubleshooting)</span>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                    <v-card-text>
                        <v-row dense align="center">
                            <v-col cols="6" sm="3" md="2">
                                <v-text-field v-model="logFilters.date_from" type="date" label="From" outlined dense hide-details clearable />
                            </v-col>
                            <v-col cols="6" sm="3" md="2">
                                <v-text-field v-model="logFilters.date_to" type="date" label="To" outlined dense hide-details clearable />
                            </v-col>
                            <v-col cols="6" sm="3" md="3">
                                <v-select v-model="logFilters.source" :items="sourceItems" label="Feature" outlined dense hide-details clearable />
                            </v-col>
                            <v-col cols="6" sm="3" md="2">
                                <v-select v-model="logFilters.status" :items="statusItems" label="Status" outlined dense hide-details clearable />
                            </v-col>
                            <v-col cols="auto">
                                <v-btn color="primary" depressed @click="reloadLogs">Apply</v-btn>
                            </v-col>
                            <v-spacer />
                            <v-col cols="auto">
                                <v-btn outlined small color="primary" :loading="exporting" @click="exportCsv">
                                    <v-icon left small>mdi-download</v-icon> Export CSV
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-tabs v-model="logTab" class="px-4">
                        <v-tab>Calls</v-tab>
                        <v-tab>By user</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="logTab">
                        <v-tab-item>
                            <v-data-table :headers="logHeaders" :items="logs" :loading="logsLoading"
                                :server-items-length="logTotal" :options.sync="logOptions"
                                :footer-props="{ 'items-per-page-options': [15, 25, 50, 100] }" dense>
                                <template v-slot:[`item.created_at`]="{ item }">{{ formatDateTime(item.created_at) }}</template>
                                <template v-slot:[`item.user_name`]="{ item }">
                                    <span v-if="item.user_name">{{ item.user_name }}</span>
                                    <span v-else class="grey--text">Background</span>
                                </template>
                                <template v-slot:[`item.source`]="{ item }">{{ sourceLabel(item.source) }}</template>
                                <template v-slot:[`item.status`]="{ item }">
                                    <v-chip x-small dark :color="item.status === 'completed' ? 'green' : 'red'">{{ item.status }}</v-chip>
                                    <div v-if="item.block_reason" class="caption grey--text">{{ item.block_reason }}</div>
                                </template>
                                <template v-slot:[`item.tokens`]="{ item }">
                                    {{ num(item.input_tokens) }} in / {{ num(item.output_tokens) }} out
                                </template>
                                <template v-slot:[`item.cost_usd`]="{ item }">${{ usd(item.cost_usd) }}</template>
                                <template v-slot:[`item.response_ms`]="{ item }">
                                    {{ item.response_ms ? (item.response_ms / 1000).toFixed(1) + 's' : '' }}
                                </template>
                            </v-data-table>
                        </v-tab-item>
                        <v-tab-item>
                            <v-data-table :headers="userHeaders" :items="byUser" :loading="logsLoading" dense
                                :items-per-page="25" sort-by="cost" sort-desc>
                                <template v-slot:[`item.user_name`]="{ item }">
                                    <span v-if="item.user_name">{{ item.user_name }}</span>
                                    <span v-else class="grey--text">Background (jobs, schedules)</span>
                                </template>
                                <template v-slot:[`item.cost`]="{ item }"><strong>${{ usd(item.cost) }}</strong></template>
                                <template v-slot:[`item.last_at`]="{ item }">{{ formatDateTime(item.last_at) }}</template>
                            </v-data-table>
                        </v-tab-item>
                    </v-tabs-items>
                </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>

            <!-- Pricing -->
            <v-col cols="12">
                <v-expansion-panels>
                    <v-expansion-panel>
                        <v-expansion-panel-header>
                            <div>
                                <v-icon left color="green darken-1">mdi-currency-usd</v-icon>
                                <strong>OpenAI pricing used for metering</strong>
                                <div class="caption grey--text mt-1">
                                    Estimated rates set in the DGTasks config (not synced from OpenAI's pricing page),
                                    so check them against OpenAI's pricing when models change.
                                </div>
                            </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <div class="caption mb-2" v-if="pricing.models">
                                Default model: <strong>{{ pricing.models.general }}</strong> ·
                                Time-tracking analysis: <strong>{{ pricing.models.analysis }}</strong>
                            </div>
                            <v-data-table :headers="pricingHeaders" :items="pricing.rows" dense :items-per-page="-1" hide-default-footer>
                                <template v-slot:[`item.model`]="{ item }">
                                    {{ item.model }}
                                    <v-chip v-if="item.overridden" x-small color="orange" dark class="ml-1">live rate</v-chip>
                                </template>
                                <template v-for="f in ['input', 'output', 'cache_read', 'cache_write']" v-slot:[`item.${f}`]="{ item }">
                                    ${{ item[f] }}
                                </template>
                            </v-data-table>
                            <div class="caption grey--text mt-2">USD per million tokens (cache read = cached input). Whisper voice transcription is estimated per minute of audio.</div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import BarChart from './BarChart';

// Friendly names for usage-log sources (unknown ones are title-cased).
const SOURCE_LABELS = {
    time_tracking_analysis: 'Time Tracking Analysis',
    sales_assessment: 'Sales Assessment',
    ai_chat: 'AI Chat',
    ai_chat_attachment_vision: 'AI Chat: Attachment Reading',
    meeting_summary: 'Meeting Summary',
    cv_analysis: 'CV Analysis',
    growth_ai: 'Employee Growth AI',
    voice_task_command: 'Voice Task Commands',
    growth_admin_insights: 'Growth Admin Insights',
    voice_transcribe: 'Voice Transcription (Whisper)',
    chat_voice_note: 'Chat Voice Notes (Whisper)',
    ai_conversation_summarizer: 'Chat: Summaries',
    ai_action_item_extractor: 'Chat: Action Items',
    ai_assistant_responder: 'Chat: Assistant Replies',
    ai_chat_search: 'Chat: Smart Search',
    ai_compose_assistant: 'Chat: Compose',
    ai_inbox_triage: 'Chat: Inbox Triage',
    ai_pmt_assistant: 'Chat: PMT Assistant',
    ai_reply_suggester: 'Chat: Reply Suggestions',
};

export default {
    name: 'AiUsageComponent',
    components: { BarChart },

    data() {
        const today = new Date();
        const iso = d => d.toISOString().slice(0, 10);
        return {
            loading: false,
            saving: false,
            breakdownLoading: false,
            snapshot: { spend_usd: 0, limit_usd: 0, remaining_usd: null, percentage: 0, source: 'env', default_limit_usd: 0 },
            limitInput: 0,

            bySource: [],
            daily: [],
            monthly: [],
            blockedToday: 0,
            weekMetric: 'cost',
            selectedPeriod: null,

            rangeFrom: iso(new Date(today.getFullYear(), today.getMonth(), 1)),
            rangeTo: iso(today),
            rangeLoading: false,
            rangeResults: null,

            logTab: 0,
            logs: [],
            byUser: [],
            logTotal: 0,
            logsLoading: false,
            logOptions: { page: 1, itemsPerPage: 15 },
            logFilters: { date_from: null, date_to: null, source: null, status: null },
            sources: [],
            exporting: false,

            pricing: { rows: [], alert_email: null, models: null },

            statusItems: [{ text: 'Completed', value: 'completed' }, { text: 'Blocked', value: 'blocked' }],
            featureHeaders: [
                { text: 'Feature', value: 'source' },
                { text: 'Calls', value: 'calls', align: 'end' },
                { text: 'Today', value: 'today', align: 'end', sortable: false },
                { text: 'This month', value: 'month', align: 'end', sortable: false },
                { text: 'All time', value: 'total_cost', align: 'end' },
            ],
            logHeaders: [
                { text: 'Time', value: 'created_at', sortable: false },
                { text: 'User', value: 'user_name', sortable: false },
                { text: 'Feature', value: 'source', sortable: false },
                { text: 'Model', value: 'model', sortable: false },
                { text: 'Status', value: 'status', sortable: false },
                { text: 'Tokens', value: 'tokens', sortable: false },
                { text: 'Cost', value: 'cost_usd', sortable: false, align: 'end' },
                { text: 'Time taken', value: 'response_ms', sortable: false, align: 'end' },
            ],
            userHeaders: [
                { text: 'User', value: 'user_name' },
                { text: 'Calls', value: 'calls', align: 'end' },
                { text: 'Input tokens', value: 'input_tokens', align: 'end' },
                { text: 'Output tokens', value: 'output_tokens', align: 'end' },
                { text: 'Cost', value: 'cost', align: 'end' },
                { text: 'Last call', value: 'last_at' },
            ],
            pricingHeaders: [
                { text: 'Model', value: 'model' },
                { text: 'Input', value: 'input', align: 'end' },
                { text: 'Output', value: 'output', align: 'end' },
                { text: 'Cache read', value: 'cache_read', align: 'end' },
                { text: 'Cache write', value: 'cache_write', align: 'end' },
            ],
        };
    },

    computed: {
        isUnlimited() {
            return !this.snapshot.limit_usd || this.snapshot.limit_usd <= 0;
        },
        barColor() {
            const p = this.snapshot.percentage;
            return p >= 100 ? 'error' : p >= 80 ? 'orange' : 'green';
        },
        canSave() {
            return this.limitInput !== '' && this.limitInput !== null && Number(this.limitInput) >= 0
                && Number(this.limitInput) !== Number(this.snapshot.limit_usd);
        },
        weekBars() {
            return this.daily.map(d => ({
                key: d.date,
                label: d.label,
                value: this.weekMetric === 'cost' ? d.total_cost : d.total_calls,
                display: this.weekMetric === 'cost' ? '$' + d.total_cost.toFixed(3) : String(d.total_calls),
                tooltip: `${d.date}: ${d.total_calls} calls, $${d.total_cost.toFixed(4)}`,
                highlight: d.isToday,
            }));
        },
        monthBars() {
            return this.monthly.map(d => ({
                key: d.date,
                label: d.label,
                value: d.total_cost,
                display: '$' + d.total_cost.toFixed(2),
                tooltip: `${d.date}: ${d.total_calls} calls, $${d.total_cost.toFixed(4)}`,
                highlight: d.isToday,
            }));
        },
        monthLabel() {
            return new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        },
        monthTotals() {
            return {
                cost: this.monthly.reduce((s, d) => s + d.total_cost, 0),
                calls: this.monthly.reduce((s, d) => s + d.total_calls, 0),
                activeDays: this.monthly.filter(d => d.total_calls > 0).length,
            };
        },
        monthProjection() {
            const days = this.monthly.length;
            if (!days || !this.monthTotals.cost) return null;
            const now = new Date();
            const inMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
            return (this.monthTotals.cost / days) * inMonth;
        },
        periods() {
            const sum = (key, f) => this.bySource.reduce((s, r) => s + r[key][f], 0);
            return [
                { key: 'today', label: 'Today', color: '#43A047', cost: sum('today', 'cost'), calls: sum('today', 'calls') },
                { key: 'week', label: 'This week', color: '#1E88E5', cost: sum('week', 'cost'), calls: sum('week', 'calls') },
                { key: 'month', label: 'This month', color: '#8E24AA', cost: sum('month', 'cost'), calls: sum('month', 'calls') },
            ];
        },
        periodFeatures() {
            if (!this.selectedPeriod) return [];
            const k = this.selectedPeriod;
            const total = this.bySource.reduce((s, r) => s + r[k].cost, 0) || 1;
            return this.bySource
                .filter(r => r[k].calls > 0)
                .map(r => ({ source: r.source, calls: r[k].calls, cost: r[k].cost, pct: Math.round(r[k].cost / total * 100) }))
                .sort((a, b) => b.cost - a.cost);
        },
        rangeBars() {
            if (!this.rangeResults) return [];
            return this.rangeResults.daily.map(d => ({
                key: d.day,
                label: d.day.slice(5),
                value: Number(d.total_cost),
                display: '$' + Number(d.total_cost).toFixed(2),
                tooltip: `${d.day}: ${d.total_calls} calls, $${Number(d.total_cost).toFixed(4)}`,
            }));
        },
        sourceItems() {
            return this.sources.map(s => ({ text: this.sourceLabel(s), value: s }));
        },
    },

    watch: {
        logOptions: {
            handler() {
                this.loadLogs();
            },
            deep: true,
        },
    },

    mounted() {
        this.loadAll();
    },

    methods: {
        loadAll() {
            this.loadSnapshot();
            this.loadBreakdown();
            this.loadLogs();
            this.loadPricing();
        },

        async loadSnapshot() {
            this.loading = true;
            const res = await this.$axios.get('api/ai-usage/snapshot');
            if (res.data?.response) {
                this.snapshot = res.data.data;
                this.limitInput = this.snapshot.limit_usd;
            } else if (res.status === 403) {
                this.$toast.error('You do not have access to AI usage.');
            }
            this.loading = false;
        },

        async saveLimit() {
            this.saving = true;
            const res = await this.$axios.post('api/ai-usage/set-limit', { limit_usd: Number(this.limitInput) });
            if (res.data?.response) {
                this.snapshot = res.data.data;
                this.limitInput = this.snapshot.limit_usd;
                this.$toast.success('Daily AI budget updated');
            } else {
                this.$toast.error(res.data?.message || 'Failed to update the budget');
            }
            this.saving = false;
        },

        async loadBreakdown() {
            this.breakdownLoading = true;
            const res = await this.$axios.get('api/ai-usage/breakdown');
            if (res.data?.response) {
                const d = res.data.data;
                this.bySource = d.by_source;
                this.daily = d.daily;
                this.monthly = d.monthly;
                this.blockedToday = d.blocked?.today_count || 0;
            }
            this.breakdownLoading = false;
        },

        async loadRange() {
            this.rangeLoading = true;
            const res = await this.$axios.post('api/ai-usage/range', { from: this.rangeFrom, to: this.rangeTo });
            if (res.data?.response) {
                this.rangeResults = res.data.data;
            } else {
                this.$toast.error(res.data?.message || 'Failed to load the range');
            }
            this.rangeLoading = false;
        },

        rangePct(f) {
            const total = this.rangeResults?.totals?.cost || 0;
            return total > 0 ? Math.round(Number(f.total_cost) / total * 100) : 0;
        },

        logParams(extra = {}) {
            const params = { ...extra };
            Object.entries(this.logFilters).forEach(([k, v]) => {
                if (v) params[k] = v;
            });
            return params;
        },

        reloadLogs() {
            if (this.logOptions.page !== 1) {
                this.logOptions = { ...this.logOptions, page: 1 }; // watcher reloads
            } else {
                this.loadLogs();
            }
        },

        async loadLogs() {
            this.logsLoading = true;
            const res = await this.$axios.get('api/ai-usage/logs', this.logParams({
                page: this.logOptions.page,
                per_page: this.logOptions.itemsPerPage > 0 ? this.logOptions.itemsPerPage : 200,
                include_users: 1,
            }));
            if (res.data?.response) {
                this.logs = res.data.data;
                this.logTotal = res.data.total;
                this.byUser = (res.data.by_user || []).map(u => ({ ...u, cost: Number(u.cost) }));
                this.sources = res.data.sources || [];
            }
            this.logsLoading = false;
        },

        async exportCsv() {
            this.exporting = true;
            const res = await this.$axios.get('api/ai-usage/export', this.logParams());
            if (typeof res.data === 'string') {
                const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `ai-usage-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                URL.revokeObjectURL(url);
            } else {
                this.$toast.error('Export failed');
            }
            this.exporting = false;
        },

        async loadPricing() {
            const res = await this.$axios.get('api/ai-usage/pricing');
            if (res.data?.response) {
                this.pricing = { rows: res.data.data, alert_email: res.data.alert_email, models: res.data.models };
            }
        },

        sourceLabel(source) {
            return SOURCE_LABELS[source]
                || String(source || 'unknown').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        },

        usd(v) {
            const n = Number(v) || 0;
            return n >= 1 ? n.toFixed(2) : n.toFixed(4);
        },

        num(v) {
            return (Number(v) || 0).toLocaleString();
        },

        formatDateTime(v) {
            if (!v) return '';
            const d = new Date(String(v).replace(' ', 'T'));
            return isNaN(d) ? v : d.toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
        },
    },
};
</script>

<style scoped>
.period-tile {
    cursor: pointer;
    transition: background 0.2s;
}
.stat-tile {
    background: #ede7f6;
    border-radius: 6px;
    padding: 10px 18px;
    text-align: center;
}
</style>
